import { MoreOutlined } from '@ant-design/icons';
import { Button, Dropdown, Empty, Spin } from 'antd';
import { useCallback, type FC } from 'react';

import { menuItems } from '@pages/Portfolio/constants';
import { usePortfolio } from '@pages/Portfolio/hooks';
import type { PortfolioItem } from '@pages/Portfolio/types';
import {
  PortfolioAddModal,
  PortfolioDeleteModal,
  PortfolioEditModal,
} from '@pages/Portfolio/ui';

import '@pages/Portfolio/styles/Portfolio.desktop.scss';

export const Portfolio: FC = () => {
  const {
    portfolios,
    isAddModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isAddLoading,
    isUpdateLoading,
    isDeleteLoading,
    editingItem,
    deletingItem,
    startDragging,
    handleDropOnIndex,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    closeAddModal,
    handleDownload,
    handleAddSubmit,
    handleEditSubmit,
    handleDeleteSubmit,
    isPortfolioLoading,
    isPortfolioFetching,
  } = usePortfolio();

  const handleMenuClick = useCallback(
    (actionKey: string, item: PortfolioItem) => {
      if (actionKey === 'download') {
        handleDownload(item);
      }
      if (actionKey === 'edit') {
        openEditModal(item);
      }
      if (actionKey === 'delete') {
        openDeleteModal(item);
      }
    },
    [handleDownload, openDeleteModal, openEditModal]
  );

  const renderCardImage = useCallback((item: PortfolioItem) => {
    const file = item.File;

    if (!file || !file.Data) {
      return (
        <div className="bc-employee-portfolio__image-placeholder">
          <span className="bc-employee-portfolio__image-placeholder-icon" />
        </div>
      );
    }

    const extension = (file.Name ?? '').split('.').pop()?.toLowerCase();
    const isPng = extension === 'png';
    const isWebp = extension === 'webp';
    const isJpeg = extension === 'jpg' || extension === 'jpeg';

    let mimeType = 'image/jpeg';

    if (isPng) {
      mimeType = 'image/png';
    } else if (isWebp) {
      mimeType = 'image/webp';
    } else if (!isJpeg) {
      mimeType = 'application/octet-stream';
    }

    const src = `data:${mimeType};base64,${file.Data}`;

    return (
      <img
        src={src}
        alt={item.Description ?? file.Name ?? 'Изображение портфолио'}
        className="bc-employee-portfolio__image"
      />
    );
  }, []);

  if (isPortfolioLoading) {
    return (
      <div className="bc-employee-portfolio__loader">
        <Spin />
      </div>
    );
  }

  return (
    <Spin spinning={isPortfolioFetching}>
      <div className="bc-employee-portfolio">
        {portfolios.length === 0 && (
          <Empty
            description="Портфолио пока пусто. Добавьте первое изображение."
            className="bc-employee-portfolio__empty"
          />
        )}

        {portfolios.length > 0 && (
          <div className="bc-employee-portfolio__grid">
            {portfolios.map((item, index) => (
              <div
                key={item.Id}
                className="bc-employee-portfolio__card"
                draggable
                onDragStart={() => startDragging(index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  void handleDropOnIndex(index);
                }}
              >
                <div className="bc-employee-portfolio__image-wrapper">
                  {renderCardImage(item)}
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: menuItems,
                      onClick: ({ key }) =>
                        handleMenuClick(key.toString(), item),
                    }}
                    placement="bottomRight"
                  >
                    <Button
                      icon={<MoreOutlined />}
                      className="bc-employee-portfolio__menu-button"
                    />
                  </Dropdown>
                </div>
                <div className="bc-employee-portfolio__description">
                  {item.Description || item.File?.Name || 'Без описания'}
                </div>
              </div>
            ))}
          </div>
        )}

        <PortfolioAddModal
          open={isAddModalOpen}
          confirmLoading={isAddLoading}
          onCancel={closeAddModal}
          onSubmit={handleAddSubmit}
          disabled={isAddLoading}
        />

        <PortfolioEditModal
          open={isEditModalOpen}
          confirmLoading={isUpdateLoading}
          portfolio={editingItem}
          onCancel={closeEditModal}
          onSubmit={handleEditSubmit}
          disabled={isUpdateLoading}
        />

        <PortfolioDeleteModal
          open={isDeleteModalOpen}
          confirmLoading={isDeleteLoading}
          portfolio={deletingItem}
          onCancel={closeDeleteModal}
          onConfirm={handleDeleteSubmit}
          disabled={isDeleteLoading}
        />
      </div>
    </Spin>
  );
};
